import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Divider,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import CronEditor from "./CronEditor";
import SelectorBuilder from "./SelectorBuilder";
import SearchableSelect from "../shared/SearchableSelect";



const CRON_EXAMPLES = [
    { label: "Toutes les heures", cron: "0 * * * *" },
    { label: "Tous les jours à minuit", cron: "0 0 * * *" },
    { label: "Tous les jours à 9h", cron: "0 9 * * *" },
    { label: "Chaque lundi à 3h", cron: "0 3 * * 1" },
    { label: "Toutes les 15 min", cron: "*/15 * * * *" },
];

const INITIAL_FORM = {
    job_key: "",
    name: "",
    description: "",
    transaction_type: "",
    segment_id: "",
    selector: {},
    payload_template: "{}",
    active: true,
    cron: "",
    timezone: "UTC",
};

const CreateInternalJobDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [loadingET, setLoadingET] = useState(false);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [segmentsLoading, setSegmentsLoading] = useState(false);

    // Fetch internal event types when dialog opens
    useEffect(() => {
        if (!open || !brandName) return;
        setLoadingET(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types?brand=${encodeURIComponent(brandName)}&origin=INTERNAL&active=true`, {
            headers: { "X-Brand": brandName }
        })
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((data) => setTransactionTypes(Array.isArray(data) ? data : []))
            .catch(() => setTransactionTypes([]))
            .finally(() => setLoadingET(false));

        setSegmentsLoading(true);
        const query = `?brand=${encodeURIComponent(brandName)}&active=true`;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/ui-options/segments${query}`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => setSegmentOptions(Array.isArray(data?.items) ? data.items : []))
            .catch(() => setSegmentOptions([]))
            .finally(() => setSegmentsLoading(false));
    }, [open, brandName]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));



    const COMMON_TIMEZONES = ["UTC", "Europe/Paris", "GMT", "Africa/Douala", "America/New_York"];

    const isValid = form.name.trim() && form.transaction_type.trim();

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            // selector is already a plain object from SelectorBuilder
            const selector = (form.selector && typeof form.selector === "object") ? form.selector : {};
            let payload_template = null;
            if (form.payload_template.trim() && form.payload_template.trim() !== "{}") {
                try { payload_template = JSON.parse(form.payload_template); }
                catch { throw new Error("Le payload_template n'est pas un JSON valide"); }
            }

            const schedule = form.cron.trim()
                ? { type: "cron", cron: form.cron.trim(), timezone: form.timezone.trim() || "UTC" }
                : undefined;

            const body = {
                brand: brandName || null,
                name: form.name.trim(),
                description: form.description.trim() || null,
                transaction_type: form.transaction_type.trim(),
                segment_id: form.segment_id ? form.segment_id : null,
                selector,
                payload_template,
                active: form.active,
                schedule: schedule || null,
            };

            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setSegmentOptions([]); setError(null); onClose(); };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.internalJobs.gradient }}>
                        <WorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Job Interne
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>



                <Grid container spacing={2}>
                    {/* name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Nom du job" value={form.name}
                            onChange={handle("name")} size="small"
                            helperText="Nom lisible du job"
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* description */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small"
                            helperText="Description courte (optionnelle)"
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* transaction_type */}
                    <Grid item xs={12}>
                        <TextField fullWidth required select label="Type de transaction (INTERNAL)" value={form.transaction_type}
                            onChange={handle("transaction_type")} size="small"
                            helperText="Doit être de type INTERNAL"
                            sx={fieldSx}
                            InputProps={{ endAdornment: loadingET ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null }}>
                            {transactionTypes.length === 0 && !loadingET
                                ? <MenuItem value="" disabled><em>Aucun type INTERNAL disponible</em></MenuItem>
                                : transactionTypes.map((et) => (
                                    <MenuItem key={et.id} value={et.key}>
                                        {et.name} <Typography component="span" fontSize={11} color="text.secondary" ml={1}>({et.key})</Typography>
                                    </MenuItem>
                                ))
                            }
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <SearchableSelect
                            label={segmentsLoading ? "Chargement des segments…" : "Ciblage par segment (optionnel)"}
                            loading={segmentsLoading}
                            options={[
                                { id: "", name: "Aucun segment (tous les clients)" },
                                ...segmentOptions,
                            ]}
                            value={form.segment_id}
                            onChange={(id) => setForm((p) => ({ ...p, segment_id: id }))}
                            getOptionValue={(s) => String(s.id)}
                            getOptionLabel={(s) => s.name || String(s.id)}
                            placeholder="Rechercher un segment…"
                            helperText={
                                segmentOptions.length === 0 && !segmentsLoading
                                    ? "Aucun segment actif — créez-en dans l'onglet Segments"
                                    : "Si renseigné, le job cible uniquement les membres du segment"
                            }
                            renderOption={(props, s) => (
                                <Box component="li" {...props}>
                                    <Typography fontSize={13} fontWeight={s.id ? 600 : 500}>
                                        {s.name || String(s.id)}
                                    </Typography>
                                    {s.id && (
                                        <Typography component="span" fontSize={11} color="text.secondary" ml={1}>
                                            ({s.is_dynamic ? "Dynamique" : "Statique"})
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* selector */}
                    <Grid item xs={12}>
                        <Typography fontSize={12} fontWeight={600} color="text.secondary" mb={1}>
                            Sélecteur de clients
                        </Typography>
                        <SelectorBuilder
                            brandName={brandName}
                            value={form.selector}
                            onChange={(val) => setForm(p => ({ ...p, selector: val }))}
                        />
                    </Grid>


                    {/* Schedule section */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }}><Typography fontSize={11} color="text.secondary">Planification du Job</Typography></Divider>
                    </Grid>

                    {/* Cron Editor */}
                    <Grid item xs={12} sm={8}>
                        <CronEditor
                            value={form.cron}
                            onChange={(val) => setForm(p => ({ ...p, cron: val }))}
                        />
                    </Grid>

                    {/* timezone */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            select
                            label="Fuseau horaire"
                            value={form.timezone}
                            onChange={(e) => setForm(p => ({ ...p, timezone: e.target.value }))}
                            size="small"
                            helperText="Ex: UTC, Europe/Paris"
                            sx={fieldSx}
                        >
                            {COMMON_TIMEZONES.map((tz) => (
                                <MenuItem key={tz} value={tz}>
                                    {tz}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>



                    {/* active */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer le job</Typography>
                                <Typography fontSize={11} color="text.secondary">Le job sera planifié et exécuté selon le CRON</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.internalJobs.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.internalJobs.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.internalJobs.gradient, color: "#fff", textTransform: "none",
                        borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.internalJobs.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer le job"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateInternalJobDialog;
